import * as React from "react"
import Image from "next/image"
import { FaUser } from "react-icons/fa"
import {
  getDriverAvatarColor,
  getDriverInitials,
} from "@/lib/drivers/presentation"

interface DriverAvatarProps {
  photoUrl?: string | null
  name?: string | null
  id: string | number
  size: number
  className?: string
  /**
   * Skip Next's image optimizer. The listing card opts in (12 tiny avatars per
   * page, Storyblok already serves sized assets); the profile page stays
   * optimized (AVIF/WebP + edge cache on its hero-adjacent photo).
   */
  unoptimized?: boolean
}

/**
 * Avatar for a driver on the public cluster. Prefers the real photo; when a
 * driver has none (allowed since MAR-646) it renders an initials monogram on a
 * deterministic per-driver color, falling back to a generic icon for nameless
 * drivers. See docs/adr/0006-present-low-signal-drivers.md.
 */
export function DriverAvatar({
  photoUrl,
  name,
  id,
  size,
  className = "",
  unoptimized = false,
}: DriverAvatarProps) {
  if (photoUrl) {
    return (
      <Image
        src={photoUrl}
        alt={name || "Driver photo"}
        width={size}
        height={size}
        className={`rounded-full object-cover ${className}`.trim()}
        unoptimized={unoptimized}
      />
    )
  }

  const initials = getDriverInitials(name)
  const style = {
    width: size,
    height: size,
    backgroundColor: initials ? getDriverAvatarColor(id) : "#9ca3af", // gray-400
  }

  return (
    <div
      style={style}
      aria-label={name || "Driver"}
      className={`flex items-center justify-center rounded-full font-semibold text-white ${className}`.trim()}
    >
      {initials ? (
        <span style={{ fontSize: size * 0.4 }}>{initials}</span>
      ) : (
        <FaUser size={size * 0.5} aria-hidden />
      )}
    </div>
  )
}
